import { ReactNode, useEffect, useState } from 'react';
import { Alert, Box, Button, ButtonProps, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import useAuthSnapshot from '../../hooks/useAuthSnapshot';

type Props = {
 url:string;
 filename:string;
 title?:string;
 body?:unknown;
 children:ReactNode;
} & Pick<ButtonProps,'variant'|'disabled'|'sx'>;

function PdfPreview({url,filename,title,body,onClose}:{url:string;filename:string;title:string;body:string|undefined;onClose:()=>void}) {
 const [pdf,setPdf]=useState<{url:string;filename:string}|null>(null),[error,setError]=useState(''),[attempt,setAttempt]=useState(0);
 const fullScreen=useMediaQuery(useTheme().breakpoints.down('sm'));
 useEffect(()=>{
  const controller=new AbortController();let objectUrl:string|undefined,active=true;
  setPdf(null);setError('');
  void (async()=>{
   try {
    const response=await fetch(url,{credentials:'include',cache:'no-store',signal:controller.signal,...(body===undefined?{}:{method:'POST',headers:{'Content-Type':'application/json'},body})});
    if(!response.ok){const e=await response.json().catch(()=>({}));throw new Error(e.message||e.error?.message||`PDF unavailable (${response.status})`);}
    if(!response.headers.get('Content-Type')?.toLowerCase().includes('application/pdf'))throw new Error('The server did not return a PDF. Please retry.');
    const blob=await response.blob();
    if(!active)return;
    if(!blob.size)throw new Error('The PDF is empty. Please retry.');
    objectUrl=URL.createObjectURL(blob);
    const savedName=response.headers.get('Content-Disposition')?.match(/filename="([^"]+)"/i)?.[1];
    setPdf({url:objectUrl,filename:savedName||filename});
   }catch(e){if(active)setError((e as Error).message);}
  })();
  return()=>{active=false;controller.abort();if(objectUrl)URL.revokeObjectURL(objectUrl);};
 },[url,body,filename,attempt]);
 return <Dialog open fullWidth maxWidth="lg" fullScreen={fullScreen} onClose={onClose} aria-label={title} PaperProps={{sx:{height:fullScreen?'100%':'90vh'}}}>
  <DialogTitle sx={{overflowWrap:'anywhere'}}>{title}</DialogTitle>
  <DialogContent sx={{display:'flex',flexDirection:'column',minHeight:0,p:{xs:1,sm:3}}}>
   {error?<Alert severity="error" action={<Button onClick={()=>setAttempt(a=>a+1)}>Retry</Button>}>{error}</Alert>:pdf?
    <><Typography variant="body2" color="text.secondary" sx={{mb:1}}>Review the PDF below, then download a copy if needed.</Typography><Box component="iframe" title="Journey Log PDF preview" src={pdf.url} sx={{width:'100%',flex:1,minHeight:0,border:0,bgcolor:'#fff'}}/><Button component="a" href={pdf.url} target="_blank" rel="noopener noreferrer" sx={{alignSelf:'flex-start'}}>Open PDF in new tab</Button></>:
    <Stack direction="row" spacing={2} alignItems="center" role="status"><CircularProgress size={24}/><Typography>Loading PDF preview…</Typography></Stack>}
  </DialogContent>
  <DialogActions sx={{flexWrap:'wrap'}}>{pdf&&<Button component="a" href={pdf.url} download={pdf.filename} variant="contained">Download PDF</Button>}<Button onClick={onClose}>Close preview</Button></DialogActions>
 </Dialog>;
}

export default function PdfPreviewButton({url,filename,title='Journey Log PDF preview',body,children,...buttonProps}:Props) {
 const {operatorRef,userData,isAuthenticated}=useAuthSnapshot(),scope=`${isAuthenticated}:${userData.id||'anonymous'}:${operatorRef}`;
 const [opened,setOpened]=useState<string|null>(null);
 // A different account, operator or document must never retain an open preview.
 const identity=JSON.stringify([scope,url,body,filename]);
 useEffect(()=>setOpened(null),[identity]);
 return <><Button {...buttonProps} onClick={()=>setOpened(identity)}>{children}</Button>{opened===identity&&<PdfPreview key={identity} url={url} filename={filename} title={title} body={body===undefined?undefined:JSON.stringify(body)} onClose={()=>setOpened(null)}/>}</>;
}
